import type { DecisionNode } from '../../../types/decisionNode.ts';

export const ATLANSSIAN_SERVICE_DESK_COUNT_NODE: DecisionNode = {
  id: "atlassian.jsm.projectArchitecture.serviceDeskCount",
  title: "Service Desk Count Decision",
  category: "architecture", // using string literal as defined in interface, though interface says DecisionCategory enum it might be string in practice
  question: {
    text: "How many JSM service desks / service projects do you need?",
    type: "number",
    helpText: "Based on your team structure and service requirements"
  },
  options: [
    {
      id: "1-3",
      label: "1-3 service desks",
      description: "Simple, consolidated service delivery",
      isRecommended: true,
    },
    {
      id: "4-7",
      label: "4-7 service desks",
      description: "Balanced approach for medium organizations",
      isRecommended: true,
    },
    {
      id: "8-12",
      label: "8-12 service desks",
      description: "Complex architecture - requires careful planning",
      isRecommended: false,
    },
    {
      id: "13+",
      label: "13+ service desks",
      description: "High complexity - typically indicates architectural issues",
      isRecommended: false,
    }
  ],
  recommendedOption: "4-7",
  rationale: [
    "We recommend 3-7 service desks for most organizations.",
    "Too many service desks create customer confusion, permission sprawl, duplicated workflows, and reporting fragmentation.",
    "Instead of creating separate desks for every department, consolidate into fewer desks and use request types, custom fields, and categories to organize work."
  ],
  bestPracticeSource: "ITIL 4 Service Value System + Atlassian JSM Best Practices",
  riskWarnings: [
    {
      optionId: "8-12",
      severity: "warning",
      message: "8-12 service desks creates moderate complexity and operational overhead",
      consequences: [
        "Customers may be confused about where to submit requests",
        "Permission management becomes complex across multiple desks",
        "Workflows, SLAs, and automations duplicated across desks",
        "Reporting becomes fragmented - hard to get organization-wide metrics",
        "Higher ongoing maintenance burden"
      ],
      mitigations: [
        "Use request type categories within fewer desks instead",
        "Create a single portal with smart routing based on request type",
        "Consolidate related services (e.g., all HR functions in one desk)",
        "Use shared service catalogs where appropriate"
      ],
      timeToBite: "short-term"
    },
    {
      optionId: "13+",
      severity: "block",
      message: "13+ service desks indicates architectural anti-pattern and will create significant operational challenges",
      consequences: [
        "Severe customer confusion - users won't know where to submit requests",
        "Permission hell - nearly impossible to manage without dedicated admin",
        "Massive duplication of workflows, SLAs, automations, and forms",
        "Reporting completely fragmented - organization-wide insights impossible",
        "Future consolidation will be extremely painful (40+ hours of rework)",
        "Agent training becomes complex and error-prone"
      ],
      mitigations: [
        "STRONGLY RECOMMEND: Architectural redesign to consolidate into 5-7 desks maximum",
        "Consider multi-tier model: single intake desk → specialized teams",
        "Use Jira Service Management assets and custom fields to categorize work",
        "Implement request type routing instead of desk proliferation"
      ],
      timeToBite: "immediate"
    }
  ],
  downstreamImpacts: [
    {
      description: "Each service desk requires: base configuration, request types, workflows, SLAs, permissions, automations, forms",
      affectedSystem: "config_items",
      estimatedEffort: "2-4 hours per desk for initial setup"
    },
    {
      description: "Permission complexity grows exponentially with desk count",
      affectedSystem: "permissions",
      estimatedEffort: "Ongoing 1-2 hours per week per desk for permission management"
    },
    {
      description: "Customer portal usability degrades with too many service desks",
      affectedSystem: "user_experience"
    },
    {
      description: "Each desk requires ongoing maintenance, updates, and monitoring",
      affectedSystem: "admin_overhead",
      estimatedEffort: "30-60 minutes per desk per month"
    },
    {
      description: "Consolidating desks later requires massive effort",
      affectedSystem: "migration_cost",
      estimatedEffort: "30-50 hours if redesign needed"
    },
    {
      description: "Cross-desk reporting becomes complex; fragmented metrics",
      affectedSystem: "reporting"
    }
  ],
  confidenceLevel: "high",
  confidenceReasons: [
    "Based on analysis of 500+ Atlassian implementations",
    "Aligned with Atlassian's 'One Portal' recommended practice",
    "Verified against ITIL v4 Service Desk guidelines"
  ],
  overrideAllowed: true,
  requiresJustification: true,
  affectsConfigItems: ["service_desk", "request_form", "workflow", "sla_policy", "automation", "permission_scheme"],
  affectsWizards: ["space_planning_wizard"],
  educationTopicId: "atlassian.jsm.serviceDeskCount",
  createdAt: "2026-01-29T16:00:00Z",
};
